import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface ImpersonationInfo {
  adminId: string;
  targetUserId: string;
  targetUserName: string;
  targetUserEmail: string;
  startedAt: string;
}

export function ImpersonationBanner() {
  const { toast } = useToast();
  const [info, setInfo] = useState<ImpersonationInfo | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem('impersonation_admin');
    if (stored) {
      try {
        setInfo(JSON.parse(stored));
      } catch {
        setInfo(null);
      }
    }

    // Listen for storage changes (e.g., when impersonation starts/stops in another tab)
    const handleStorage = () => {
      const data = localStorage.getItem('impersonation_admin');
      setInfo(data ? JSON.parse(data) : null);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Also set CSS variable for layout offset
  useEffect(() => {
    if (info) {
      document.documentElement.style.setProperty('--impersonation-banner-height', '40px');
    } else {
      document.documentElement.style.setProperty('--impersonation-banner-height', '0px');
    }
  }, [info]);

  if (!info) return null;

  const handleStopImpersonation = () => {
    // Restore admin token
    const adminToken = sessionStorage.getItem('impersonation_admin_token');
    if (adminToken) {
      localStorage.setItem('access_token', adminToken);
    }

    // Clean up impersonation data
    sessionStorage.removeItem('impersonation_admin_token');
    sessionStorage.removeItem('impersonation_session_id');
    localStorage.removeItem('impersonation_admin');

    toast({ title: 'Returned to admin session' });

    // Redirect to admin users page
    window.location.href = '/admin/users';
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 flex h-10 items-center justify-center gap-3 bg-amber-500 px-4 text-amber-950 text-sm font-medium">
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="truncate">
        You are logged in as <strong>{info.targetUserName}</strong>
        {info.targetUserEmail && ` (${info.targetUserEmail})`}
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-7 shrink-0 border-amber-700 bg-amber-600 text-amber-50 hover:bg-amber-700 hover:text-white text-xs"
        onClick={handleStopImpersonation}
      >
        Return to Admin
      </Button>
    </div>
  );
}
